import { useRef, useState } from 'react';
import { downloadCsv, downloadPng, EXPORT_IGNORE_ATTR, EXPORT_ROOT_ATTR, exportFileName, tableToRows, type CsvRow } from '../exportUtils';

const iconButton =
  'flex h-11 w-11 items-center justify-center rounded-input text-text-muted transition-colors duration-150 hover:bg-surface-alt hover:text-text-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue disabled:cursor-not-allowed disabled:opacity-50';

/**
 * CSV and PNG download buttons for the card they sit in (the closest
 * `data-export-root` ancestor). CSV comes from `getRows` when given, else from
 * the first table in the card, so it matches what is on screen.
 */
export function ExportMenu({ title, getRows, className = '' }: { title: string; getRows?: () => CsvRow[] | null; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const root = () => ref.current?.closest<HTMLElement>(`[${EXPORT_ROOT_ATTR}]`) ?? null;

  const onCsv = () => {
    const table = root()?.querySelector('table');
    const rows = getRows?.() ?? (table ? tableToRows(table) : null);
    if (rows) downloadCsv(rows, exportFileName(title));
  };

  const onPng = async () => {
    const element = root();
    if (!element) return;
    setBusy(true);
    try {
      await downloadPng(element, exportFileName(title));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} {...{ [EXPORT_IGNORE_ATTR]: '' }} className={`flex shrink-0 items-center ${className}`}>
      <button type="button" onClick={onCsv} aria-label={`Download ${title} as CSV`} title="Download CSV" className={iconButton}>
        <i aria-hidden="true" className="fa-solid fa-file-csv text-base" />
      </button>
      <button type="button" onClick={() => void onPng()} disabled={busy} aria-label={`Download ${title} as PNG`} title="Download PNG" className={iconButton}>
        <i aria-hidden="true" className={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-image'} text-base`} />
      </button>
    </div>
  );
}
